import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageTitle from "../components/PageTitle";

const sections = [
  {
    title: "Aircraft Technical Status",
    text: "Before commencement of a flight, the operating crew can review the current technical status of the assigned aircraft. Technical information remains associated with the specific aircraft registration and is carried forward between sectors.",
    items: [
      {
        src: "/images/etlb/aircraft-status-report.png",
        title: "Technical status overview",
        caption:
          "The status overview identifies open defects, deferred items, aircraft downgrades or limitations, scheduled maintenance events, technical notices and the latest release-to-service information.",
      },
      {
        src: "/images/etlb/flight-history.png",
        title: "Aircraft technical history",
        caption:
          "The history provides a chronological record of aircraft acceptance, servicing entries and flight closure activity, including the responsible user and recorded time.",
      },
    ],
  },
  {
    title: "Minimum Equipment List Reference",
    text: "The e-TLB includes a searchable Minimum Equipment List reference. It supports identification of the applicable item and provides the associated aircraft applicability, rectification interval, dispatch conditions and operational procedures.",
    items: [
      {
        src: "/images/etlb/mel-item.png",
        title: "MEL item details",
        caption:
          "Each MEL item displays the rectification interval, installed and required quantities, placard requirement, applicability, remarks and conditions.",
      },
      {
        src: "/images/etlb/mel-procedures.png",
        title: "MEL operational procedure",
        caption:
          "Associated MEL operational procedures provide the limitations, tables and procedural actions applicable to the selected aircraft type.",
      },
      {
        src: "/images/etlb/mel-entry.png",
        title: "ECAM alert lookup",
        caption:
          "MEL entries can be located using the ECAM alert description or ATA chapter to support assessment of the reported technical condition.",
      },
    ],
  },
  {
    title: "Technical Defect Reporting",
    text: "Technical defects identified during aircraft operation can be recorded against the affected registration. Each entry becomes part of the aircraft technical record and remains available for subsequent assessment, deferral, rectification or maintenance action.",
    items: [
      {
        src: "/images/etlb/create-complaint.png",
        title: "New defect entry",
        caption:
          "Pilot and cabin defects are recorded by ATA chapter with a structured description. Related historical reports are displayed to support consistent defect identification.",
      },
      {
        src: "/images/etlb/dispatch-complaint.png",
        title: "Dispatch assessment and deferral",
        caption:
          "The assessment records the relevant MEL or deferred-defect reference, deferral category, transfer baseline and remaining time, flight-hour or flight-cycle limit.",
      },
      {
        src: "/images/etlb/workorder-detail.png",
        title: "Workorder record",
        caption:
          "The workorder retains the defect status, maintenance planning information, deferral data and completed actions in chronological order.",
      },
    ],
  },
  {
    title: "Flight and Servicing Data",
    text: "",
    items: [
      {
        src: "/images/etlb/preflight-tasks-open.png",
        title: "Outstanding pre-flight tasks",
        caption:
          "The task overview presents the operational actions required for the active flight, including flight data, open items, fuel uplift, oil data, the pre-flight check and the applicable security task.",
      },
      {
        src: "/images/etlb/fuel-uplift.png",
        title: "Fuel uplift entry",
        caption:
          "The fuel record captures fuel type, density and uplift in metric or imperial units and compares the calculated quantity with the recorded departure fuel.",
      },
      {
        src: "/images/etlb/oil-data.png",
        title: "Engine oil entry",
        caption:
          "Engine oil quantities are recorded for each engine and assessed against the configured operating ranges for the aircraft type.",
      },
      {
        src: "/images/etlb/preflight-tasks-complete.png",
        title: "Completed pre-flight tasks",
        caption:
          "Completed actions remain visible with their recorded status, providing the crew with a clear overview of the pre-flight workflow and the information entered for the active sector.",
      },
      {
        src: "/images/etlb/postflight-data.png",
        title: "Post-flight data entry",
        caption:
          "The sector record includes off-block, take-off, landing and on-block times together with calculated block time, flight time and landing totals.",
      },
    ],
  },
];

function EtlbFigure({ item, onOpen }) {
  return (
    <figure className="etlb-figure">
      <button
        type="button"
        className="etlb-figure-image"
        onClick={() => onOpen(item)}
        aria-label={`Enlarge ${item.title}`}
      >
        <img src={item.src} alt={item.title} loading="lazy" decoding="async" />
        <span aria-hidden="true">View full size</span>
      </button>
      <figcaption>
        <strong>{item.title}</strong>
        <span>{item.caption}</span>
      </figcaption>
    </figure>
  );
}

export default function EtlbPage() {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!selectedImage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedImage(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedImage]);

  return (
    <>
      <PageTitle>vWZZ - Electronic Technical Logbook</PageTitle>
      <div className="page-container etlb-page">
        <header className="etlb-page-header">
          <h1 className="page-title">vWZZ Electronic Technical Logbook</h1>
          <p className="page-subtitle">
            The vWZZ Electronic Technical Logbook (e-TLB) is used to record and
            manage the technical status of aircraft operated within the virtual
            airline.
          </p>
        </header>

        <section className="etlb-introduction" aria-labelledby="etlb-intro-title">
          <div>
            <h2 id="etlb-intro-title">Overview</h2>
            <p>
              The e-TLB provides continuity between individual flights by
              associating technical defects, deferred items, maintenance actions
              and aircraft status with the specific aircraft registration.
            </p>
            <p>
              This allows each aircraft to retain an individual technical
              history rather than returning to a default technical condition
              after each simulator session.
            </p>
          </div>
          <img
            src="/images/etlb/aircraft-status-report.png"
            alt="vWZZ e-TLB aircraft technical status overview"
          />
        </section>

        <section className="etlb-video-section" aria-labelledby="etlb-video-title">
          <div className="etlb-section-copy">
            <h2 id="etlb-video-title">MEL Operational Procedure Example</h2>
            <p>
              The recording demonstrates review of a detailed MEL operational
              procedure (OPS PROC), including aircraft applicability, technical
              tables, operating limitations and the specific procedural actions
              associated with the selected item.
            </p>
          </div>
          <video
            controls
            playsInline
            preload="metadata"
            poster="/images/etlb/preflight-tasks-complete.png"
            aria-label="vWZZ e-TLB MEL operational procedure demonstration"
          >
            <source src="/video/etlb-workflow.mp4" type="video/mp4" />
            Your browser does not support embedded MP4 video.
          </video>
        </section>

        {sections.map((section) => (
          <section className="etlb-detail-section" key={section.title}>
            <header>
              <div>
                <h2>{section.title}</h2>
                <p>{section.text}</p>
              </div>
            </header>
            <div className={`etlb-gallery etlb-gallery-${section.items.length}`}>
              {section.items.map((item) => (
                <EtlbFigure
                  item={item}
                  key={item.src}
                  onOpen={setSelectedImage}
                />
              ))}
            </div>
          </section>
        ))}

        <section className="etlb-closing">
          <div>
            <h2>Operational Use</h2>
            <p>
              The vWZZ e-TLB is intended exclusively for virtual airline and
              flight simulation operations. It supports structured simulation of
              aircraft technical-log processes and does not replace approved
              manufacturer, operator or regulatory documentation used in
              real-world aviation.
            </p>
          </div>
          <Link className="btn btn-primary" to="/apply">
            Join vWZZ
          </Link>
        </section>

        {selectedImage && (
          <div
            className="etlb-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedImage.title} full-size image`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectedImage(null);
            }}
          >
            <button
              type="button"
              className="etlb-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close full-size image"
              autoFocus
            >
              Close
            </button>
            <figure>
              <img src={selectedImage.src} alt={selectedImage.title} />
              <figcaption>
                <strong>{selectedImage.title}</strong>
                <span>{selectedImage.caption}</span>
              </figcaption>
            </figure>
          </div>
        )}
      </div>
    </>
  );
}
